import { useState, useEffect } from "react"
import { Button } from "@/shared/components/ui/button"
import { PackageCard } from "@/features/client/components/packages/PackageCard"
import { Link } from "react-router-dom"
import { api, type PackageData } from "@/shared/lib/api/ApiClient"
import type { TravelPackage } from "@/shared/types/packages"

// Adapter function to convert API data to TravelPackage format
function adaptToTravelPackage(apiPackage: PackageData): TravelPackage {

  
  return {
    id: apiPackage.id,
    title: apiPackage.title,
    location: apiPackage.location,
    duration: parseInt(apiPackage.duration.charAt(0)) || 1, // Extract number from "3D/2N" format
    days: apiPackage.days,
    nights: apiPackage.nights,
    category: apiPackage.category,
    currency: apiPackage.currency,
    price: typeof apiPackage.price === 'string' ? parseFloat(apiPackage.price) : apiPackage.price,
    image: apiPackage.image || '/logo.svg',
    visaRequired: Boolean(apiPackage.visa_required),
    visaInfo: apiPackage.visa_info || '',
    overview: apiPackage.overview || '',
    highlights: apiPackage.packageHighlights || [],
    packageHighlights: apiPackage.packageHighlights || [],
    itinerary: (apiPackage.itinerary || []).map(day => ({
      ...day,
      accommodation: day.accommodation || "",
    })),
    inclusions: apiPackage.inclusions || [],
    exclusions: apiPackage.exclusions || []
  }
}

export function Featured() {
  const [featuredPackages, setFeaturedPackages] = useState<TravelPackage[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchFeaturedPackages = async () => {
      try {
        setLoading(true)
        // Add cache-busting parameter to get fresh data
        const response = await api.packages.getAll({ 
          status: "active",
          _t: Date.now() // Cache buster
        })
        
        if (response.success && response.data) {
          const packages = response.data.map(adaptToTravelPackage)
          // Get first 3 packages as featured
          setFeaturedPackages(packages.slice(0, 3))
        } else {
          setError('Failed to load packages')
        }
      } catch (err) {
        console.error('Error fetching featured packages:', err)
        setError('Error loading featured packages')
      } finally {
        setLoading(false)
      }
    }

    fetchFeaturedPackages()
    
    // Auto-refresh featured packages every 5 minutes to catch updates
    const interval = setInterval(fetchFeaturedPackages, 300000) // 5 minutes
    
    return () => clearInterval(interval)
  }, [])

  return (
    <section className="py-16 sm:py-20 lg:py-24 px-4 bg-background">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16 animate-in fade-in duration-700">
          <div className="inline-block bg-orange-100 text-orange-600 px-5 py-2.5 rounded-full text-sm font-semibold mb-6 shadow-sm">
            Featured Destinations
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-balance">
            Handpicked <span className="text-orange-500">Adventures</span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto text-pretty leading-relaxed">
            Discover our carefully curated selection of extraordinary destinations, each offering unique experiences and
            unforgettable memories
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 rounded-lg h-64 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <Button
              onClick={() => window.location.reload()}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Try Again
            </Button>
          </div>
        ) : featuredPackages.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">No featured packages available at the moment.</p>
            <Button asChild>
              <Link to="/destination">Browse All Destinations</Link>
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {featuredPackages.map((pkg, index) => (
                <div key={pkg.id} className="animate-in slide-in-from-bottom duration-500" style={{ animationDelay: `${index * 100}ms` }}>
                  <PackageCard package={pkg} />
                </div>
              ))}
            </div>

            <div className="text-center mt-12 animate-in fade-in duration-700" style={{ animationDelay: '400ms' }}>
              <Button
                asChild
                size="lg"
                variant="ghost"
                className="border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white bg-transparent px-10 py-6 text-lg font-semibold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-[transform,shadow,background-color,color] duration-300"
              >
                <Link to="/destination">
                  Explore All Destinations →
                </Link>
              </Button>
            </div>
          </>
        )}
      </div>
    </section>
  )
}
